import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { decode } from "js-base64";
import * as ActivateAccountScreenUI from "../ui/ActivateAccountScreenUI";
import zxcvbn, { ZXCVBNResult } from "zxcvbn";
import { useAPI } from "../../apiv1";
import { Error } from "../../types/apiv1";
import { useAppDispatch } from "../../store/hooks";
import { login } from "../../store/thunk/auth";

function passwordStrength(password: string): ZXCVBNResult {
	return zxcvbn(password, [
		"mit", "beredskab", "mitberedskab", "mb",
	]);
}

type PendingUserPrefilledFields = {
	given_name: string,
	middle_names: string,
	family_name: string,
}

type PendingUser = {
	request_id: string,
	token: string,
	email: string,
	prefilled_fields: PendingUserPrefilledFields,
}

function isPendingUser(obj: any): obj is PendingUser {
	return (
		obj != null &&
		typeof obj === 'object' &&
		typeof obj['request_id'] === 'string' &&
		typeof obj['token'] === 'string' &&
		typeof obj['email'] === 'string' &&
		obj['prefilled_fields'] &&
		typeof obj['prefilled_fields'] === 'object' &&
		typeof obj['prefilled_fields']['given_name'] === 'string' &&
		typeof obj['prefilled_fields']['middle_names'] === 'string' &&
		typeof obj['prefilled_fields']['family_name'] === 'string' &&
		(obj['prefilled_fields']['date_of_birth'] === null ||
		typeof obj['prefilled_fields']['date_of_birth'] === 'string')
	);
}

function ActivateAccount({ pendingUser }: { pendingUser: PendingUser }) {
	const api = useAPI();
	const dispatch = useAppDispatch();

	const [pending, setPending] = useState(false);
	const [error, setError] = useState<ActivateAccountScreenUI.RejectionReason | null>(null);
	const [done, setDone] = useState(false);
	
	const [givenName, setGivenName] = useState(pendingUser.prefilled_fields.given_name);
	const [middleNames, setMiddleNames] = useState(pendingUser.prefilled_fields.middle_names);
	const [familyName, setFamilyName] = useState(pendingUser.prefilled_fields.family_name);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const strength = useMemo(() => passwordStrength(password), [password]);

	const allowSubmit =
		strength.score > 2 &&
		password === confirmPassword;

	async function submit() {
		setPending(true);
		try {
			await api.users.post(
				pendingUser.request_id,
				pendingUser.token,
				givenName,
				middleNames,
				familyName,
				password,
			);
			setDone(true);
			// try to log the user automatically in
			dispatch(login(pendingUser.email, password));
		} catch (e) {
			switch (e) {
			case Error.Network:
				setError(ActivateAccountScreenUI.RejectionReason.Network);
				break;
			case Error.Forbidden:
				setError(ActivateAccountScreenUI.RejectionReason.Invalid);
				break;
			case Error.Conflict:
				setError(ActivateAccountScreenUI.RejectionReason.Conflict);
				break;
			default:
				setError(ActivateAccountScreenUI.RejectionReason.Bug);
				break;
			}
		} finally {
			setPending(false);
		}
	}

	if (done) {
		return <ActivateAccountScreenUI.DoneScreen />;
	}
	if (error === ActivateAccountScreenUI.RejectionReason.Invalid ||
		error === ActivateAccountScreenUI.RejectionReason.Conflict) {
		// If we got one of these errors there is no point in letting
		// the user try again.
		return <ActivateAccountScreenUI.ErrorScreen rejectionReason={error} />;
	}
	return <ActivateAccountScreenUI.InputScreen
		pending={pending}
		rejectionReason={error}
		onSubmit={allowSubmit ? submit : undefined}

		passwordStrength={strength.score}

		givenName={{
			value: givenName, setValue: setGivenName,
		}}
		familyName={{
			value: familyName, setValue: setFamilyName,
		}}

		password={{
			value: password, setValue: setPassword,
		}}
		confirmPassword={{
			value: confirmPassword, setValue: setConfirmPassword,
			error: confirmPassword !== "" && password !== confirmPassword
				? ActivateAccountScreenUI.ConfirmPasswordErrors.Mismatch
				: undefined
		}}
	/>;
}

export default function ActivateAccountURLDecoder() {
	const encodedData = useLocation().hash.substring(1);
	
	// JavaScript strings use UTF-8. The base64 data however, is encoded
	// from a UTF-8 string. This casuses window.atob to spit out garbage
	// when using non-ASCII characters. The js-base64 library handles UTF-8
	// correctly, so we are using that instead.
	const pendingUser = useMemo(() => {
		try {
			return JSON.parse(decode(encodedData))
		} catch (e) {
			// The JSON is invalid.
			return {};
		}
	}, [encodedData]);

	if (!isPendingUser(pendingUser)) {
		return <ActivateAccountScreenUI.ErrorScreen
			rejectionReason={ActivateAccountScreenUI.RejectionReason.Invalid} />;
	}

	return <ActivateAccount pendingUser={pendingUser} />;
}
